import { useState } from "react";

import { useToast } from "../../../hooks/useToast";
import { serverErrorMessage } from "../../../utils/HttpError";
import {
  AdminSelectField,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  ReadOnlyValue,
} from "../shared";

interface IRenameMasterKeyModalProps {
  translationKey: string;
  currentUsedLocation: string;
  usedLocations: string[];
  isSaving: boolean;
  onRename: (newKey: string, newUsedLocation: string) => Promise<void>;
  onClose: () => void;
}

/**
 * Rename a master key — Retool's `modalEditKey` (EN-5178). Applies the new
 * key (and used location) to every language row that carries it. The API
 * rejects a rename onto a key that already exists.
 */
export function RenameMasterKeyModal({
  translationKey,
  currentUsedLocation,
  usedLocations,
  isSaving,
  onRename,
  onClose,
}: IRenameMasterKeyModalProps) {
  const { showToast } = useToast();

  const [newKey, setNewKey] = useState(translationKey);
  const [newUsedLocation, setNewUsedLocation] = useState(currentUsedLocation);

  const changed =
    newKey.trim() !== translationKey || newUsedLocation !== currentUsedLocation;
  const canSubmit = newKey.trim() !== "" && newUsedLocation !== "" && changed;

  const handleRename = async () => {
    if (!canSubmit) return;

    try {
      await onRename(newKey.trim(), newUsedLocation);
      showToast("The key was updated successfully.", "success");
      onClose();
    } catch (error) {
      showToast(
        serverErrorMessage(error) ?? "There was an error updating this key.",
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Edit key</ModalTitle>

      <ModalForm>
        <FilterGroup>
          <FilterLabel>Current key</FilterLabel>
          <ReadOnlyValue css={{ overflowWrap: "anywhere" }}>
            {translationKey}
          </ReadOnlyValue>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="master-rename-key">New key</FilterLabel>
          <FilterInput
            id="master-rename-key"
            type="text"
            value={newKey}
            disabled={isSaving}
            onChange={(e) => setNewKey(e.target.value)}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="master-rename-location">Location</FilterLabel>
          <AdminSelectField
            id="master-rename-location"
            value={newUsedLocation}
            disabled={isSaving}
            onChange={(e) => setNewUsedLocation(e.target.value)}
          >
            {usedLocations.map((location) => (
              <option key={location} value={location}>
                {location}
              </option>
            ))}
          </AdminSelectField>
          <FieldHint>
            Applies to every language of this key. Remember to re-sync the
            environments afterwards — a renamed key is a NEW key to them.
          </FieldHint>
        </FilterGroup>

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isSaving}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={!canSubmit || isSaving}
            onClick={handleRename}
          >
            {isSaving ? "Saving..." : "Save"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
